import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Tooltip, Typography } from '@mui/material';
import { EmployeeDTO } from 'probonio-shared-ui/api';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_SERVICE_FIELD, EmployeeDuplicateField, mapEmployeeDuplicate } from '../employeeDuplicates';

interface Props {
  employee: EmployeeDTO;
  field: EmployeeDuplicateField;
  /** 'right' matches the value column of the profile card, 'left' the table cells. */
  align?: 'left' | 'right';
  bold?: boolean;
}

/**
 * Shows a mail or employee number and marks it when it is part of a duplicate. Falls back to the
 * account service value when the employee has none of its own, so the value never just disappears.
 */
export const DuplicateValue: React.FC<Props> = ({ employee, field, align = 'left', bold }) => {
  const { t } = useTranslation('usersModule');
  const duplicate = mapEmployeeDuplicate(employee, field);
  const ownValue = employee[field];

  // no duplicate: render exactly what the call sites rendered before. Without an own value the
  // account service value still stands in - it is simply adopted on the next sync, so no warning.
  if (!duplicate) {
    if (ownValue) {
      return <div>{ownValue}</div>;
    }
    const accountServiceValue = employee[ACCOUNT_SERVICE_FIELD[field]] as string | undefined;
    return accountServiceValue ? (
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', textAlign: align }}>
        {accountServiceValue}
      </Typography>
    ) : null;
  }

  const isConflict = duplicate.state === 'CONFLICT';
  // on a mismatch the current value is the one that should win, so it stays the primary one
  const isMismatch = duplicate.state === 'MISMATCH';
  const shownValue = isMismatch && ownValue ? ownValue : duplicate.value;
  const justify = align === 'right' ? 'flex-end' : 'flex-start';

  return (
    // everything stays inline: the profile card renders this inside a Typography (a <p>), so a div
    // anywhere in here would be invalid HTML - display:block gives the block layout instead
    <Box component="span" data-test-id={`duplicate-value-${field}`} sx={{ display: 'block', minWidth: 0 }}>
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: justify, gap: 0.5, minWidth: 0 }}>
        {/* same as the truncated plain value: hovering reveals what the ellipsis cuts off */}
        <Tooltip title={shownValue} enterTouchDelay={0} placement="top">
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: bold ? 'bold' : undefined,
              // the incoming value is not the employee's own yet, so it is set apart
              fontStyle: isMismatch ? undefined : 'italic',
              color: isConflict ? 'error.main' : isMismatch ? undefined : 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              // the box is exactly max-content wide, so overflow:hidden would shave the italic
              // overhang of the last glyph off - this gives that ink somewhere to land
              paddingRight: '0.25em',
            }}
          >
            {shownValue}
          </Typography>
        </Tooltip>
        {/* icon after the value so the label below lines up with the value itself */}
        <Tooltip arrow title={t(`duplicates.tooltip.${duplicate.state}.${field}`, { value: duplicate.value })}>
          <WarningAmberIcon
            color={isConflict ? 'error' : 'warning'}
            sx={{ flexShrink: 0, fontSize: '1rem' }}
            data-test-id={`duplicate-warning-icon-${field}`}
          />
        </Tooltip>
      </Box>
      <Typography
        variant="caption"
        component="span"
        sx={{ display: 'block', color: isConflict ? 'error.main' : 'warning.main', textAlign: align }}
        data-test-id={`duplicate-${isConflict ? 'conflict' : 'differs'}-label-${field}`}
      >
        {t(`duplicates.${isConflict ? 'conflictLabel' : isMismatch ? 'mismatchLabel' : 'differsLabel'}.${field}`)}
      </Typography>
    </Box>
  );
};
